import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Stack,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import EditIcon from '@mui/icons-material/Edit';
import PolicyIcon from '@mui/icons-material/Policy';
import { toast } from 'react-toastify';
import { leaveApi } from './leaveApi';

export default function LeaveTypePolicyManagement() {
  const [types, setTypes] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  // Type modal
  const [openTypeModal, setOpenTypeModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeForm, setTypeForm] = useState({
    code: '',
    name: '',
    category: 'CASUAL',
    description: '',
    is_paid: true,
    is_statutory: false,
    is_active: true,
  });

  // Policy modal
  const [openPolicyModal, setOpenPolicyModal] = useState(false);
  const [selectedTypeForPolicy, setSelectedTypeForPolicy] = useState(null);
  const [policyForm, setPolicyForm] = useState({
    name: '',
    accrual_frequency: 'MONTHLY',
    accrual_rate: '1.25',
    max_balance_cap: '30.00',
    carry_forward_max_days: '30.00',
    encashment_allowed: false,
    encashment_min_balance: '0.00',
    allow_half_day: true,
    min_service_days_required: 0,
    max_consecutive_days: 15,
    min_days_notice: 0,
    document_mandatory_threshold: 0,
    allow_negative_balance: false,
    negative_balance_limit: '0.00',
    is_active: true,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tData, pData] = await Promise.all([
        leaveApi.getTypes(),
        leaveApi.getPolicies(),
      ]);
      setTypes(tData);
      setPolicies(pData);
    } catch (err) {
      toast.error('Failed to load leave types or policies.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSeedTemplates = async () => {
    try {
      setSeeding(true);
      const res = await leaveApi.seedTemplates();
      toast.success(`Seeded ${res.types_created} leave types and ${res.policies_created} statutory policies!`);
      fetchData();
    } catch (err) {
      toast.error('Failed to seed reference templates.');
    } finally {
      setSeeding(false);
    }
  };

  const handleOpenTypeModal = (type = null) => {
    if (type) {
      setEditingType(type);
      setTypeForm({
        code: type.code,
        name: type.name,
        category: type.category,
        description: type.description,
        is_paid: type.is_paid,
        is_statutory: type.is_statutory,
        is_active: type.is_active,
      });
    } else {
      setEditingType(null);
      setTypeForm({
        code: '',
        name: '',
        category: 'CASUAL',
        description: '',
        is_paid: true,
        is_statutory: false,
        is_active: true,
      });
    }
    setOpenTypeModal(true);
  };

  const handleSaveType = async () => {
    try {
      if (editingType) {
        await leaveApi.updateType(editingType.id, typeForm);
        toast.success('Leave type updated.');
      } else {
        await leaveApi.createType(typeForm);
        toast.success('Leave type created.');
      }
      setOpenTypeModal(false);
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.code?.[0] || 'Error saving leave type.';
      toast.error(msg);
    }
  };

  const handleOpenPolicyModal = (leaveType) => {
    setSelectedTypeForPolicy(leaveType);
    const existing = policies.find((p) => p.leave_type === leaveType.id);
    if (existing) {
      setPolicyForm(existing);
    } else {
      setPolicyForm({
        name: `${leaveType.name} Policy`,
        accrual_frequency: 'MONTHLY',
        accrual_rate: '1.25',
        max_balance_cap: '30.00',
        carry_forward_max_days: '30.00',
        encashment_allowed: false,
        encashment_min_balance: '0.00',
        allow_half_day: true,
        min_service_days_required: 0,
        max_consecutive_days: 15,
        min_days_notice: 0,
        document_mandatory_threshold: 0,
        allow_negative_balance: false,
        negative_balance_limit: '0.00',
        is_active: true,
      });
    }
    setOpenPolicyModal(true);
  };

  const handleSavePolicy = async () => {
    try {
      const existing = policies.find((p) => p.leave_type === selectedTypeForPolicy.id);
      const payload = {
        ...policyForm,
        leave_type: selectedTypeForPolicy.id,
      };
      if (existing) {
        await leaveApi.updatePolicy(existing.id, payload);
        toast.success('Leave policy updated.');
      } else {
        await leaveApi.createPolicy(payload);
        toast.success('Leave policy configured.');
      }
      setOpenPolicyModal(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to save policy.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
            Leave Categories & Accrual Policies
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Configure tenant leave types, statutory entitlements, and periodic accrual rules.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={seeding ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
            onClick={handleSeedTemplates}
            disabled={seeding}
            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
          >
            {seeding ? 'Seeding...' : 'Seed Statutory Baseline Templates'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenTypeModal()}
            sx={{ borderRadius: '12px', bgcolor: '#2563eb', textTransform: 'none', fontWeight: 700 }}
          >
            Add Leave Type
          </Button>
        </Stack>
      </Box>

      {/* Types Table */}
      <TableContainer component={Paper} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Nature</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Accrual Policy</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Status</TableCell>
              <TableCell align="center" sx={{ fontWeight: 800, color: '#475569' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {types.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6, color: '#64748b' }}>
                  No leave types configured. Click "Seed Statutory Baseline Templates" to populate defaults.
                </TableCell>
              </TableRow>
            ) : (
              types.map((t) => {
                const pol = policies.find((p) => p.leave_type === t.id);
                return (
                  <TableRow key={t.id} hover>
                    <TableCell sx={{ fontWeight: 700 }}>
                      <Chip label={t.code} size="small" sx={{ fontWeight: 800, bgcolor: '#e0f2fe', color: '#0369a1' }} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t.name}</TableCell>
                    <TableCell sx={{ color: '#64748b' }}>{t.category}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          label={t.is_paid ? 'Paid' : 'Unpaid'}
                          size="small"
                          sx={{
                            bgcolor: t.is_paid ? '#f0fdf4' : '#fef2f2',
                            color: t.is_paid ? '#16a34a' : '#ef4444',
                            fontWeight: 700,
                          }}
                        />
                        {t.is_statutory && (
                          <Chip label="Statutory" size="small" sx={{ bgcolor: '#fef3c7', color: '#b45309', fontWeight: 700 }} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {pol ? (
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                          {pol.accrual_frequency} ({pol.accrual_rate} d/period)
                        </Typography>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#dc2626', fontStyle: 'italic' }}>
                          No policy linked
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={t.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="Configure Policy">
                          <IconButton size="small" onClick={() => handleOpenPolicyModal(t)} sx={{ color: '#2563eb' }}>
                            <PolicyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Type">
                          <IconButton size="small" onClick={() => handleOpenTypeModal(t)} sx={{ color: '#475569' }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Leave Type Modal */}
      <Dialog open={openTypeModal} onClose={() => setOpenTypeModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{editingType ? 'Edit Leave Type' : 'Add Leave Type'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Type Code (e.g. EL, CL, SL)"
            value={typeForm.code}
            onChange={(e) => setTypeForm({ ...typeForm, code: e.target.value.toUpperCase() })}
            disabled={Boolean(editingType)}
          />
          <TextField
            fullWidth
            size="small"
            label="Name (e.g. Earned Leave)"
            value={typeForm.name}
            onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select
              value={typeForm.category}
              label="Category"
              onChange={(e) => setTypeForm({ ...typeForm, category: e.target.value })}
            >
              <MenuItem value="EARNED">Earned Leave (EL/PL)</MenuItem>
              <MenuItem value="CASUAL">Casual Leave (CL)</MenuItem>
              <MenuItem value="SICK">Sick / Medical Leave (SL/ML)</MenuItem>
              <MenuItem value="MATERNITY">Maternity Leave (ML)</MenuItem>
              <MenuItem value="PATERNITY">Paternity Leave (PL)</MenuItem>
              <MenuItem value="COMP_OFF">Compensatory Off</MenuItem>
              <MenuItem value="BEREAVEMENT">Bereavement Leave</MenuItem>
              <MenuItem value="MARRIAGE">Marriage Leave</MenuItem>
              <MenuItem value="LOSS_OF_PAY">Loss of Pay / Unpaid</MenuItem>
              <MenuItem value="SPECIAL">Special Leave</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            label="Description"
            value={typeForm.description}
            onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })}
          />
          <Stack direction="row" spacing={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={typeForm.is_paid}
                  onChange={(e) => setTypeForm({ ...typeForm, is_paid: e.target.checked })}
                />
              }
              label="Paid Leave"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={typeForm.is_statutory}
                  onChange={(e) => setTypeForm({ ...typeForm, is_statutory: e.target.checked })}
                />
              }
              label="Statutory Mandate"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenTypeModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveType}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Policy Modal */}
      <Dialog open={openPolicyModal} onClose={() => setOpenPolicyModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          Configure Accrual Policy: {selectedTypeForPolicy?.name} ({selectedTypeForPolicy?.code})
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Policy Name"
            value={policyForm.name}
            onChange={(e) => setPolicyForm({ ...policyForm, name: e.target.value })}
          />
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Accrual Frequency</InputLabel>
              <Select
                value={policyForm.accrual_frequency}
                label="Accrual Frequency"
                onChange={(e) => setPolicyForm({ ...policyForm, accrual_frequency: e.target.value })}
              >
                <MenuItem value="MONTHLY">Monthly Accrual</MenuItem>
                <MenuItem value="QUARTERLY">Quarterly Accrual</MenuItem>
                <MenuItem value="HALF_YEARLY">Half-Yearly Accrual</MenuItem>
                <MenuItem value="ANNUAL_UPFRONT">Annual Upfront Credit</MenuItem>
                <MenuItem value="NONE">No Automatic Accrual (Grant/Event)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Rate per Period (Days)"
              value={policyForm.accrual_rate}
              onChange={(e) => setPolicyForm({ ...policyForm, accrual_rate: e.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Max Balance Cap"
              value={policyForm.max_balance_cap || ''}
              onChange={(e) => setPolicyForm({ ...policyForm, max_balance_cap: e.target.value })}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Max Carry-Forward (Days)"
              value={policyForm.carry_forward_max_days || ''}
              onChange={(e) => setPolicyForm({ ...policyForm, carry_forward_max_days: e.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Min Notice Required (Days)"
              value={policyForm.min_days_notice || 0}
              onChange={(e) => setPolicyForm({ ...policyForm, min_days_notice: parseInt(e.target.value, 10) })}
            />
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Max Consecutive Days"
              value={policyForm.max_consecutive_days || ''}
              onChange={(e) => setPolicyForm({ ...policyForm, max_consecutive_days: e.target.value })}
            />
          </Stack>
          <Stack direction="row" spacing={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={policyForm.allow_half_day}
                  onChange={(e) => setPolicyForm({ ...policyForm, allow_half_day: e.target.checked })}
                />
              }
              label="Allow Half-Day Requests"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={policyForm.encashment_allowed}
                  onChange={(e) => setPolicyForm({ ...policyForm, encashment_allowed: e.target.checked })}
                />
              }
              label="Encashment Allowed"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenPolicyModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePolicy}>
            Save Policy
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
